import { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  COACHING_MEDITATION_DISCLAIMER,
  COACHING_MEDITATION_INTENTION_LABELS,
  type CoachingMeditationIntention,
} from '@mlm/shared/coaching';
import { linkButton } from '../components/panels/today';
import { Block, Chip, ErrorNote, Loading, Row, Screen } from '../components/primitives';
import { MoreWithPlan, ProLock } from '../components/pro-lock';
import { SteppedSession } from '../components/stepped-session';
import { coaching, type Meditation } from '../lib/api';
import { useMeditations, useFeatureLocked } from '../lib/queries';
import { ToolFrame } from './tool-frame';

const intentionLabel = (i: string) =>
  COACHING_MEDITATION_INTENTION_LABELS[i as CoachingMeditationIntention] ?? i;

/** The guided-meditation library. Intention first, like breathwork and mantras. */
export function MeditationsPage() {
  const locked = useFeatureLocked('meditation');
  const library = useMeditations();
  const [intention, setIntention] = useState<string | null>(null);

  if (locked) {
    return (
      <Screen back="/align" eyebrow="Meditation" title="How do you want to spend the time?">
        <ProLock feature="meditation" />
      </Screen>
    );
  }
  if (library.isPending) return <Loading />;
  if (library.isError)
    return <ErrorNote error={library.error} retry={() => void library.refetch()} />;

  const { meditations, intentions, proOnlyCount } = library.data;
  const offered = intentions.filter((i) => meditations.some((m) => m.intention === i));
  const shown = intention ? meditations.filter((m) => m.intention === intention) : meditations;

  return (
    <Screen
      back="/align"
      eyebrow="Meditation"
      title="How do you want to spend the time?"
      subtitle="Each session is a few steps, one at a time, on a timer you can pause."
    >
      <div className="flex flex-wrap gap-2 border-b border-border px-5 py-5">
        <Chip selected={intention === null} onClick={() => setIntention(null)}>
          All
        </Chip>
        {offered.map((i) => (
          <Chip key={i} selected={intention === i} onClick={() => setIntention(i)}>
            {intentionLabel(i)}
          </Chip>
        ))}
      </div>
      {shown.map((m) => (
        <Row
          key={m.id}
          title={m.title}
          meta={`${intentionLabel(m.intention)} · ${m.minutes} min · ${m.steps.length} steps`}
          to={`/align/meditations/${encodeURIComponent(m.id)}`}
        />
      ))}
      <MoreWithPlan count={proOnlyCount} noun="session" />
      <p className="px-5 py-6 text-xs leading-relaxed text-muted-foreground">
        {COACHING_MEDITATION_DISCLAIMER}
      </p>
    </Screen>
  );
}

export function MeditationSessionPage() {
  const { id = '' } = useParams();
  const locked = useFeatureLocked('meditation');
  const library = useMeditations();

  if (locked) {
    return (
      <ToolFrame close="/align/meditations">
        <ProLock feature="meditation" />
      </ToolFrame>
    );
  }
  if (library.isPending) return <Loading />;
  if (library.isError)
    return <ErrorNote error={library.error} retry={() => void library.refetch()} />;
  const meditation = library.data.meditations.find((m) => m.id === id);
  if (!meditation) {
    return (
      <ToolFrame close="/align/meditations">
        <Block
          title="This session is not available"
          action={
            <Link to="/align/meditations" className={linkButton}>
              Choose another
            </Link>
          }
        >
          It may have been changed or removed. Choose another session.
        </Block>
      </ToolFrame>
    );
  }
  return <Session meditation={meditation} />;
}

function Session({ meditation }: { meditation: Meditation }) {
  const fire = useCallback(() => {
    coaching.event('meditation_completed', {
      meditation: meditation.id,
      minutes: meditation.minutes,
    });
  }, [meditation]);

  return (
    <ToolFrame close="/align/meditations">
      <SteppedSession
        eyebrow={`Meditation · ${intentionLabel(meditation.intention)}`}
        title={meditation.title}
        intro={meditation.description}
        meta={`${meditation.minutes} min · ${meditation.steps.length} steps`}
        steps={meditation.steps}
        practice="meditation"
        event={fire}
        backTo="/align/meditations"
        disclaimer={COACHING_MEDITATION_DISCLAIMER}
      />
    </ToolFrame>
  );
}
